import React, { createContext, useState, useEffect, useCallback, ReactNode } from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface Account {
  id?: number;
  accountHeadName: string;
  accountType: string;
  openingBalance: number;
  openingBalanceDate: string;
  createdOn?: string;
  [key: string]: any;
}

interface AccountsContextType {
  accounts: Account[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchAccounts: (page?: number, perPage?: number) => Promise<void>;
  addAccount: (account: any) => Promise<void>;
  editAccount: (account: any) => Promise<void>;
  deleteAccount: (id: number) => Promise<void>;
}

export const AccountsContext = createContext<AccountsContextType | undefined>(undefined);

interface AccountsProviderProps {
  children: ReactNode;
}

const AccountsProvider: React.FC<AccountsProviderProps> = ({ children }) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch all accounts with pagination
  const fetchAccounts = useCallback(async (page: number = 1, perPage: number = 15): Promise<void> => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/accounts?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let accountsData: Account[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          // Nested structure: response.data.data.data
          accountsData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          // Direct array: response.data.data
          accountsData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          accountsData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        accountsData = responseData.data;
      } else if (Array.isArray(responseData)) {
        accountsData = responseData;
      }
      
      setAccounts(Array.isArray(accountsData) ? accountsData : []);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
      setAccounts([]); // Set empty array on error
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new account
  const addAccount = async (account: any): Promise<void> => {
    try {
      const response = await api.post("/accounts", account);
      const newAccount = response.data?.data || response.data;
      if (newAccount) {
        setAccounts((prevAccounts) => [...prevAccounts, newAccount]);
      }
    } catch (error) {
      console.error("Failed to add account:", error);
      throw error; // Re-throw to allow component to handle error
    }
  };

  // Edit an existing account
  const editAccount = async (account: any): Promise<void> => {
    try {
      const response = await api.put(`/accounts/${account.id}`, account);
      const updatedAccount = response.data?.data || response.data;
      if (updatedAccount) {
        setAccounts((prevAccounts) =>
          prevAccounts.map((acc) => (acc.id === account.id ? updatedAccount : acc))
        );
      }
    } catch (error) {
      console.error("Failed to edit account:", error);
      throw error; // Re-throw to allow component to handle error
    }
  };

  // Delete an account
  const deleteAccount = async (id: number): Promise<void> => {
    try {
      await api.delete(`/accounts/${id}`);
      setAccounts((prevAccounts) => prevAccounts.filter((acc) => acc.id !== id));
    } catch (error) {
      console.error("Failed to delete account:", error);
      throw error; // Re-throw to allow component to handle error
    }
  };

  useEffect(() => {
    fetchAccounts(1, 15);
  }, [fetchAccounts]);

  return (
    <AccountsContext.Provider
      value={{ accounts, pagination, loading, fetchAccounts, addAccount, editAccount, deleteAccount }}
    >
      {children}
    </AccountsContext.Provider>
  );
};

export default AccountsProvider;
